import React, { useEffect, useRef, useState } from "react";
import InputField from "../../components/input";
import CustomSelect from "../../components/select";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import {
  BHX_ACCESS_TOKEN,
  BUCKETLIST_ACCESS_TOKEN,
  BUKETLIST_ACCESS_TOKEN,
  ENDPOINT,
  getQueryParams,
  hashData,
  validateEmail,
} from "../../config";
import { Toaster, toast } from "sonner";
import DateInputField from "../../components/date-picker";
import ReactPixel from "react-facebook-pixel";

import styled, { keyframes } from "styled-components";

const ButtonWrapper = styled.button`
  position: relative;
  background-color: #fe664e;
  color: white;
  padding: 8px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  @media (max-width: 576px) {
    padding: 12px;
    font-size: 16px;
  }

  &:hover {
    background-color: #e0553f;
    color: white;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #e0553f;
    cursor: not-allowed;
    color: white;
  }
`;

// Spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  position: absolute;
  width: 14px;
  height: 14px;
  left: 60%;
  right: 50%;
  bottom: 50%;
  top: 25%;
  border: 2px solid #f3f3f3;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  margin-left: 8px;
`;

const playStoreLink =
  "https://play.google.com/store/apps/details?id=com.bhx.app";
const appStoreLink =
  "https://apps.apple.com/in/app/exco-travel-community/id6739699261"; // Replace with actual App Store link

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const fieldOfWorkChoices = [
  { label: "Design", value: 1 },
  { label: "UI/UX & Product Design", value: 2 },
  { label: "Music", value: 3 },
  { label: "Film & Video Production", value: 4 },
  { label: "Photography & Visual Arts", value: 5 },
  { label: "Writing, Journalism & Content Creation", value: 6 },
  { label: "Art & Culture", value: 7 },
  { label: "Startups & Entrepreneurship", value: 8 },
  { label: "Marketing, Branding & Growth", value: 9 },
  { label: "Investments, Venture Capital & Finance", value: 10 },
  { label: "Social Enterprises & Impact Ventures", value: 11 },
  { label: "Technology & Software Development", value: 12 },
  { label: "AI, Web3 & Emerging Tech", value: 13 },
  { label: "Science & Research", value: 14 },
  { label: "Architecture & Interior Design", value: 15 },
  { label: "Events & Experiential Production", value: 16 },
  { label: "Manufacturing & Industrial Design", value: 17 },
  { label: "Fashion & Apparel", value: 18 },
  { label: "Wellness, Fitness & Health Tech", value: 19 },
  { label: "Sustainability & Environmental Innovation", value: 20 },
  { label: "Academics & Research", value: 21 },
  { label: "Coaching, Personal Development & Leadership", value: 22 },
  { label: "Other", value: 23 },
];

const revenueChoices = [
  { label: "0 to 10L", value: 1 },
  { label: "10 to 25L", value: 2 },
  { label: "25 to 50L", value: 3 },
  { label: "50L to 1Cr", value: 4 },
  { label: "1Cr to 3Cr", value: 5 },
  { label: "3Cr to 5Cr", value: 6 },
  { label: "5Cr to 10Cr", value: 7 },
  { label: "10Cr to 20Cr", value: 8 },
  { label: "20Cr+", value: 9 },
];

const categoryChoices = [
  {
    label: "Founder / Startup Owner (Scaling a company, leading a team)",
    value: 1,
  },
  {
    label: "Freelancer / Consultant (Working independently on projects)",
    value: 2,
  },
  {
    label:
      "Creative / Artist / Designer (Cultural, creative, or artistic work)",
    value: 3,
  },
  {
    label: "Business Owner (Self-Employed) (Running a solo or small business)",
    value: 4,
  },
  {
    label: "Family Business Leader (Actively managing a family enterprise)",
    value: 5,
  },
  {
    label: "Corporate Professional (Working within a structured organization)",
    value: 6,
  },
  {
    label:
      "Exiting Corporate / Transitioning (Looking to leave or change careers)",
    value: 7,
  },
  {
    label: "Explorer / Still Figuring It Out (Not in a fixed career path yet)",
    value: 8,
  },
];

const genderOptions = [
  { label: "Male", value: 2 },
  { label: "Female", value: 1 },
  { label: "Nonbinary", value: 9 },
  { label: "Trans Woman", value: 6 },
  { label: "Trans Man", value: 7 },
  { label: "Genderqueer / Gender Nonconforming", value: 3 },
  { label: "Prefer not to say", value: 5 },
  { label: "Other (please specify)", value: 8 },
];

const phoneCode = [
  { code: "+1", country: "United States", flag: "🇺🇸" },
  { code: "+1", country: "Canada", flag: "🇨🇦" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+7", country: "Kazakhstan", flag: "🇰🇿" },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+36", country: "Hungary", flag: "🇭🇺" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+40", country: "Romania", flag: "🇷🇴" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+51", country: "Peru", flag: "🇵🇪" },
  { code: "+52", country: "Mexico", flag: "🇲🇽" },
  { code: "+53", country: "Cuba", flag: "🇨🇺" },
  { code: "+54", country: "Argentina", flag: "🇦🇷" },
  { code: "+55", country: "Brazil", flag: "🇧🇷" },
  { code: "+56", country: "Chile", flag: "🇨🇱" },
  { code: "+57", country: "Colombia", flag: "🇨🇴" },
  { code: "+58", country: "Venezuela", flag: "🇻🇪" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+90", country: "Turkey", flag: "🇹🇷" },
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+93", country: "Afghanistan", flag: "🇦🇫" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+212", country: "Morocco", flag: "🇲🇦" },
  { code: "+213", country: "Algeria", flag: "🇩🇿" },
  { code: "+216", country: "Tunisia", flag: "🇹🇳" },
  { code: "+218", country: "Libya", flag: "🇱🇾" },
  { code: "+220", country: "Gambia", flag: "🇬🇲" },
  { code: "+221", country: "Senegal", flag: "🇸🇳" },
  { code: "+234", country: "Nigeria", flag: "🇳🇬" },
  { code: "+250", country: "Rwanda", flag: "🇷🇼" },
  { code: "+254", country: "Kenya", flag: "🇰🇪" },
  { code: "+255", country: "Tanzania", flag: "🇹🇿" },
  { code: "+256", country: "Uganda", flag: "🇺🇬" },
  { code: "+260", country: "Zambia", flag: "🇿🇲" },
  { code: "+263", country: "Zimbabwe", flag: "🇿🇼" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+352", country: "Luxembourg", flag: "🇱🇺" },
  { code: "+353", country: "Ireland", flag: "🇮🇪" },
  { code: "+354", country: "Iceland", flag: "🇮🇸" },
  { code: "+358", country: "Finland", flag: "🇫🇮" },
  { code: "+370", country: "Lithuania", flag: "🇱🇹" },
  { code: "+371", country: "Latvia", flag: "🇱🇻" },
  { code: "+372", country: "Estonia", flag: "🇪🇪" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+381", country: "Serbia", flag: "🇷🇸" },
  { code: "+385", country: "Croatia", flag: "🇭🇷" },
  { code: "+386", country: "Slovenia", flag: "🇸🇮" },
];

const RequestInvite = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    nationality: "",
    designation: "",
    // jobTitle: "",
    industry: "",
    instagramProfile: "",
    linkedinProfile: "https://www.linkedin.com/",

    portfolio: "",
    countryCode: "+91",
    phoneNumber: "",
    email: "",
    hasReferral: false,
    referralCode: "",
    contactMethod: [],
    howHeard: "",

    relevant_category: "",
    annual_revenue: "",
    top_reasons: [],
    top_areas: [],
    city: "",
  });
  const [cityLoading, setCityLoading] = useState(false);
  const [activeCityPage, setActiveCityPage] = useState(1);
  const [cities, setCities] = useState([]);

  const [userChoiceLoading, setUserChoiceLoading] = useState(false);
  const [userChoices, setUserChoices] = useState({});
  const [errors, setErrors] = useState({
    phoneNumber: "",
    email: "",
  });

  // Get editionData from the context passed from the parent
  const { editionData } = useOutletContext();

  // console.log(id, "id");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false); // Track focus state

  const [isBooked, setIsBooked] = useState(false);
  const [bookingError, setBookingError] = useState(false);

  const [ip, setIp] = useState(null);
  const { project, otherParams } = getQueryParams();

  // console.log(otherParams, "otherParams");

  const handleInputChange = (e) => {
    setSearchTerm(e);
    setMenuOpen(e.length > 0); // Open menu only when input has text
  };

  const handleCityChange = (value) => {
    handleSelectChange(value, "city");
    const val = cities.find((city) => city.value === value) || null;
    console.log(value, val, "val");
    setSearchTerm(val.label); // Set selected value as input text
    setMenuOpen(false); // Close dropdown
    setIsFocused(false);
  };

  // console.log(searchTerm, isFocused, menuOpen, "searchTerm");

  const isFirstLoad = useRef(true);
  const inputRef = useRef(null); // Reference for the input field

  useEffect(() => {
    getClientIp();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 700);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm !== "") {
      getCities();
    }
  }, [debouncedSearchTerm, activeCityPage]);

  useEffect(() => {
    if (editionData?.project_name) {
      getUserChoices();
    }
  }, [editionData?.project_name]);

  const getClientIp = async () => {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    console.log(data, "ip_data");
    setIp(data.ip);
    // return data.ip; // Return the IP address
  };

  // console.log(menuOpen, "menuOpen");
  // Ensure input displays selected value when menu is closed
  // useEffect(() => {
  //   if (!isFocused && formData.city) {
  //     console.log("isFocused", isFocused);

  //     const selectedCity = cities.find((city) => city.value === formData.city);
  //     console.log(searchTerm, "searchTerm");
  //     console.log("selectedCity", selectedCity);
  //     setSearchTerm(selectedCity ? selectedCity.label : "");
  //   }
  // }, [isFocused, formData.city, menuOpen]);

  const getCities = async () => {
    setCityLoading(true);
    try {
      let params = new URLSearchParams({
        limit: debouncedSearchTerm ? 0 : 10,
        offset: debouncedSearchTerm ? 0 : (activeCityPage - 1) * 10,
        phone_code: "91",
        search: debouncedSearchTerm || "",
      });

      // Only add default search params on initial load
      // if (isFirstLoad.current) {
      //   params.append("default_city", 10);
      // }

      // const limitNew = debouncedSearchTerm ? 0 : 10;
      // const offset = debouncedSearchTerm ? 0 : (activeCityPage - 1) * 10;

      const response = await fetch(
        // `${ENDPOINT}/api/country/state/city/?phone_code=91&limit=${limitNew}&offset=${offset}&search=${debouncedSearchTerm}&default_city=1`
        `${ENDPOINT}/api/country/state/city/?${params}`
      );

      const data = await response.json();

      const newOptions = data?.data?.map((city) => ({
        value: city.id,
        label: city.name,
      }));

      setCities((prevOptions) => {
        const combined = [
          ...(debouncedSearchTerm ? [] : prevOptions),
          ...newOptions,
        ];
        const uniqueOptions = Array.from(
          new Set(combined.map((opt) => opt.value))
        ).map((id) => combined.find((opt) => opt.value === id));

        return uniqueOptions;
      });

      setCityLoading(false);
    } catch (error) {
      toast.error(error.message);
      setCityLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const getUserChoices = async () => {
    setUserChoiceLoading(true);
    try {
      const response = await fetch(
        `${ENDPOINT}/api/accounts/user-choices/?project_name=${editionData?.project_name}`
      );

      const data = await response.json();

      setUserChoices(data);

      setUserChoiceLoading(false);
    } catch (error) {
      toast.error(error.message);
      setUserChoiceLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue = name === "top_reasons" ? Number(value) : value;

    // console.log(name, value, type, checked);
    if (type === "checkbox") {
      if (
        name === "contactMethod" ||
        name === "top_reasons" ||
        name === "top_areas"
      ) {
        setFormData((prevFormData) => {
          if (checked) {
            return {
              ...prevFormData,
              [name]: [...prevFormData[name], parsedValue],
            };
          } else {
            return {
              ...prevFormData,
              [name]: prevFormData[name].filter(
                (method) => method !== parsedValue
              ), // Remove value if unchecked
            };
          }
        });
      } else {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: checked,
        }));
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSelectChange = (value, name) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = (formData) => {
    if (!formData.firstName) {
      return { field: "firstName", message: "Please enter your first name." };
    }
    if (!formData.lastName) {
      return { field: "lastName", message: "Please enter your last name." };
    }
    if (!formData.gender) {
      return { field: "gender", message: "Please select your gender." };
    }
    if (!formData.dateOfBirth) {
      return {
        field: "dateOfBirth",
        message: "Please enter your date of birth.",
      };
    }

    if (!formData.designation) {
      return {
        field: "designation",
        message: "Please enter your designation.",
      };
    }
    if (!formData.industry) {
      return {
        field: "industry",
        message: "Please select your field of work.",
      };
    }

    if (!formData.relevant_category) {
      return {
        field: "relevant_category",
        message: "Please choose a relevant category.",
      };
    }
    if (!formData.annual_revenue) {
      return {
        field: "annual_revenue",
        message: "Please enter your annual revenue.",
      };
    }

    if (!formData.bio) {
      return {
        field: "bio",
        message: "Please write your intentions & story.",
      };
    }

    if (formData.bio.length < 160) {
      return {
        field: "bio",
        message:
          "Please write at least 160 characters in your intentions & story.",
      };
    }

    if (formData.top_reasons.length < 2) {
      return {
        field: "top_reasons",
        message: "Please select at least two reason to attend the experience.",
      };
    }
    if (formData.top_areas.length < 2) {
      return {
        field: "top_areas",
        message: "Please select at least two growth areas.",
      };
    }

    // if (!formData.instagramProfile) {
    //   return {
    //     field: "instagramProfile",
    //     message: "Please enter your Instagram profile link.",
    //   };
    // }
    // if (!formData.linkedinProfile) {
    //   return {
    //     field: "linkedinProfile",
    //     message: "Please enter your LinkedIn profile link.",
    //   };
    // }

    // if (!formData.portfolio) {
    //   return { field: "portfolio", message: "Please provide a portfolio link." };
    // }

    if (!formData.phoneNumber) {
      return {
        field: "phoneNumber",
        message: "Please enter your phone number.",
      };
    }

    if (formData.phoneNumber.length < 7) {
      return {
        field: "phoneNumber",
        message: "Phone number should be a minimum of 7 digits.",
      };
    }

    if (formData.phoneNumber.length > 12) {
      return {
        field: "phoneNumber",
        message: "Phone number should be a maximum of 12 digits.",
      };
    }

    if (!formData.email) {
      return { field: "email", message: "Please enter your email address." };
    }

    // if (formData.contactMethod.length === 0) {
    //   return {
    //     field: "contactMethod",
    //     message: "Please select at least one preferred contact method.",
    //   };
    // }

    if (!formData.howHeard) {
      return {
        field: "howHeard",
        message: "Let us know how you heard about us.",
      };
    }

    // If no validation errors, return null
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm(formData);

    if (validationError) {
      // Show only the latest error message
      // Swal.fire({
      //   position: "top-end",
      //   icon: "warning",
      //   title: validationError.message,
      //   showConfirmButton: false,
      //   // timer: 1500,
      // });
      toast.warning(validationError.message);
      return;
    } else {
      setSending(true);
    }

    const body = {
      phone: `${formData?.countryCode}${formData?.phoneNumber}`,
      first_name: formData?.firstName,
      last_name: formData?.lastName,
      date_of_birth: formData.dateOfBirth.split("/").reverse().join("-"),
      // date_of_birth: new Date(formData.dateOfBirth).toLocaleDateString("en-CA"),
      gender: formData.gender === "male" ? 1 : 2,
      email: formData.email,
      user_profile: {
        instagram_url: formData.instagramProfile.includes(
          "https://www.instagram.com/"
        )
          ? formData.instagramProfile
          : `https://www.instagram.com/${formData.instagramProfile}`,
        linkedin_url: formData.linkedinProfile,
        bio: formData.bio,
        nationality: formData.nationality || "Indian",
        city: formData?.city,
      },
      user_work_details: [
        {
          name: formData?.firstName,
          // work_url: formData?.designation,
          designation: formData.jobTitle,
          field_work: formData.industry,
          category: formData?.relevant_category,
          revenue: formData?.annual_revenue,
        },
      ],
      customer_marketing_info: {
        keep_in_touch_by: formData?.contactMethod.join(","), // phone, text, email
        how_did_you_hear_about_us: formData?.howHeard,
      },
    };

    const body2 = {
      phone: formData?.countryCode + formData?.phoneNumber,
      edition: id, // Assume `id` is available in your context
      growth_areas: formData?.top_areas,
      top_priority: formData?.top_reasons,
      intentions: formData?.bio,
      ...(formData?.referralCode && formData?.hasReferral
        ? { referral_code: formData?.referralCode }
        : {}),
    };

    // console.log(body, "body");
    // console.log(body2, "body2");

    try {
      // Call the first API (create-user)
      const userResponse = await axios.post(
        `${ENDPOINT}/customer/api/web/create-user/`,
        // "https://dev-api.theexperience.co/customer/api/web/create-user/",
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Now call the second API (send-booking-request)
      const bookingResponse = await axios.post(
        `${ENDPOINT}/customer/api/booking/web/send-booking-request/`,
        body2,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log(userResponse, "userRespons");

      console.log(bookingResponse, "bookingResponse");

      const eventTime = Math.floor(new Date().getTime() / 1000);

      const rawFields = {
        email: formData.email,
        phone: formData.phone,
        dob: formData.dateOfBirth,
        first_name: formData.firstName,
        city: formData.city,
        country: formData.country,
        gender: formData.gender,
        user_id: bookingResponse.data?.user_id,
        client_ip: ip,
        user_agent: navigator.userAgent,
        event_time: eventTime,
        // access_token: formData.access_token,
      };

      // // Log the raw fields to see the unhashed data
      // console.log("Raw Data:", rawFields);

      // Now create the hashed fields object
      const hashedFields = {
        email: hashData(formData.email),
        phone: hashData(formData.phone),
        dob: hashData(formData.dateOfBirth),
        first_name: hashData(formData.firstName),
        city: hashData(formData.city),
        country: hashData(formData.country),
        gender: hashData(formData.gender),
        user_id: hashData(bookingResponse.user_id),
        // client_ip: hashData(ip),
        // user_agent: hashData(navigator.userAgent),

        client_ip: ip,
        user_agent: navigator.userAgent,

        event_time: eventTime,
        // access_token: hashData(formData.access_token),
      };

      // Log the hashed fields to compare
      console.log("Hashed Data:", hashedFields);

      if (editionData?.project_name === "BHX") {
        const bhxData = { ...hashedFields, access_token: BHX_ACCESS_TOKEN };
        ReactPixel.trackSingle(
          "630019181040627",
          "Submit Application",
          bhxData
        );
      } else if (editionData?.project_name === "Bucketlist") {
        const buketListData = {
          ...hashedFields,
          access_token: BUCKETLIST_ACCESS_TOKEN,
        };
        ReactPixel.trackSingle(
          "751008092884896",
          "Submit Application",
          buketListData
        );
      }
      // Extract event_key properly
      const eventKey = bookingResponse.data?.meta_event_key;

      if (!eventKey) {
        console.error("Error: event_key is missing from bookingResponse!");
      } else {
        console.log("Extracted Event Key:", eventKey);
      }

      if (bookingResponse?.data?.user_id && eventKey) {
        const eventBody = {
          event_key: eventKey,
          payload: { ...rawFields },
        };

        const utmBody = {
          ss_source: otherParams?.ss_source,
          ss_campaign_name: otherParams?.ss_campaign_name,
          ss_adset_name: otherParams?.ss_adset_name,
          ss_ad_name: otherParams?.ss_ad_name,
          ss_campaign_id: otherParams?.ss_campaign_id,
          ss_adset_id: otherParams?.ss_adset_id,
          ss_ad_id: otherParams?.ss_ad_id,
          user: bookingResponse?.data?.user_id,
          booking: bookingResponse?.data?.booking_id,
        };

        // console.log(eventBody, "eventbody");

        await axios.post(
          `${ENDPOINT}/v2/customer/api/events/web/evnet-data-upload/`,
          eventBody,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (otherParams?.ss_source) {
          await axios.post(`${ENDPOINT}/api/utm/`, utmBody, {
            headers: {
              "Content-Type": "application/json",
            },
          });
        }

        setTimeout(() => {
          navigate("/thankyou");
        }, 500);
      }

      setSending(false);
    } catch (error) {
      setSending(false);
      // Handle errors (both request and response errors) for both API calls
      if (error.response) {
        // Server responded with a status code outside of 2xx
        const errorResponse = error.response.data;

        Swal.fire({
          icon: "error",
          title: "Something is not right",
          html: errorResponse.message,
        });

        // Swal.fire({
        //   icon: "error",
        //   title: "Something is not right",
        //   html: errorResponse.message
        //     .split("\n") // Split the message into lines
        //     .map((line) => line.trim()) // Trim spaces from each line
        //     .filter((line) => line.length > 0) // Remove empty lines
        //     .map((line) => `• ${line}`) // Add bullet point
        //     .join("<br>"), // Join with HTML line breaks
        // });
      } else if (error.request) {
        // No response was received from the server
        Swal.fire({
          icon: "error",
          title: "No Response",
          text: "The server did not respond. Please try again later.",
        });
      } else {
        // Something else went wrong during the request setup
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Something went wrong during the request.",
        });
      }

      console.error("Error calling API:", error);
    }
  };

  // Function to detect iOS or Android
  const getStoreLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return appStoreLink; // iOS (iPhone, iPad)
    } else if (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 0) {
      return appStoreLink; // macOS with touch (MacBook, iPad Pro)
    } else if (/android/i.test(userAgent)) {
      return playStoreLink; // Android
    } else if (/Win/.test(userAgent)) {
      return playStoreLink; // Windows
    } else if (/Macintosh/.test(userAgent)) {
      return appStoreLink; // Other macOS devices (MacBook, iMac)
    }

    return playStoreLink;
  };

  const validateFields = async () => {
    // setTimeout(() => {
    //   setErrors({
    //     phoneNumber: "phone number is not valid",
    //     email: "email is not valid",
    //   });
    // }, 1000);
    const isValidEmail = validateEmail(formData?.email);
    const isValidPhone = formData?.phoneNumber?.length >= 7;

    if (!isValidEmail || !isValidPhone) {
      // setErrors({
      //   email: !isValidEmail ? "Enter a valid email address" : "",
      //   phoneNumber: !isValidPhone
      //     ? "Phone number must be at least 7 digits"
      //     : "",
      // });
      return;
    }

    try {
      const response = await axios.post(
        `${ENDPOINT}/customer/api/booking/web/check-booking/`,
        {
          edition: editionData?.id,
          ...(formData?.email
            ? {
                email: formData?.email,
              }
            : {}),
          ...(formData?.phoneNumber
            ? {
                phone: `${formData?.countryCode}${formData?.phoneNumber}`,
              }
            : {}),
          // email: formData.email,
          // phone: `${formData?.countryCode}${formData?.phoneNumber}`,
        }
      );

      console.log(response, "response");

      if (response.status === 200) {
        if (response?.data?.is_show_message) {
          setErrors({
            phoneNumber:
              "You have already submitted a booking request for this edition",
            email:
              "You have already submitted a booking request for this edition",
          });
        } else {
          setErrors({ phoneNumber: "", email: "" });
        }
        setBookingError(response?.data?.is_show_message);
      } else {
        setBookingError(false);
        setErrors({ phoneNumber: "", email: "" });
      }
    } catch (error) {
      setBookingError(false);
      console.log(error, "error");
      setErrors({ phoneNumber: "", email: "" });
    }
  };

  const validateFields1 = async () => {
    // setTimeout(() => {
    //   setErrors({
    //     phoneNumber: "phone number is not valid",
    //     email: "email is not valid",
    //   });
    // }, 1000);
    if (formData?.email && formData?.phoneNumber) {
      try {
        const response = await axios.post(
          `${ENDPOINT}/customer/api/booking/web/check-booking/`,
          {
            edition: editionData?.id,
            ...(formData?.email
              ? {
                  email: formData?.email,
                }
              : {}),
            ...(formData?.phoneNumber
              ? {
                  phone: `${formData?.countryCode}${formData?.phoneNumber}`,
                }
              : {}),
            // email: formData.email,
            // phone: `${formData?.countryCode}${formData?.phoneNumber}`,
          }
        );

        console.log(response, "response");

        if (response.status === 200) {
          if (response?.data?.is_show_message) {
            setErrors({
              phoneNumber:
                formData?.phoneNumber &&
                "You have already submited booking request for this edition",
              email:
                formData?.email &&
                "You have already submited booking request for this edition",
            });
          } else {
            setErrors({ phoneNumber: "", email: "" });
          }
          setBookingError(response?.data?.is_show_message);
        } else {
          setBookingError(false);
          setErrors({ phoneNumber: "", email: "" });
        }
      } catch (error) {
        setBookingError(false);
        console.log(error, "error");
        // setErrors({
        //   phoneNumber:
        //     formData?.phone && error?.response?.data?.message.includes("phone")
        //       ? "Enter Valid Phone Number"
        //       : "",
        //   email:
        //     formData?.email && error?.response?.data?.message.includes("email")
        //       ? "Enter Valid Email "
        //       : "",
        // });
        setErrors({
          phoneNumber: "",
          email: "",
        });
      }
    }
  };

  const handleFocus = (e) => {
    // Move cursor to end of input
    const val = e.target.value;
    e.target.value = "";
    e.target.value = val;
  };

  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters

    if (value.length > 8) value = value.slice(0, 8); // Limit to 8 characters

    let formattedValue = "";
    if (value.length > 4) {
      formattedValue = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(
        4
      )}`;
    } else if (value.length > 2) {
      formattedValue = `${value.slice(0, 2)}/${value.slice(2)}`;
    } else {
      formattedValue = value;
    }

    setFormData({
      ...formData,
      [e.target.name]: formattedValue,
    });
  };

  return (
    <div>
      <div className="row">
        <div className="col-md-12">
          <div className="container py-5  rounded">
            <div className="text-center mb-5">
              {/* <h1 className="fw-bold text-primary">
                Request Your Invite for F1 Monaco Grand Prix & French Riviera
              </h1>
              <p className="text-muted fs-5">
                Join our tribe of 5,000+ creators, artists, and travelers for an
                unforgettable experience.
              </p> */}
            </div>

            <div className="row">
              <div className="col-md-4  pe-lg-5">
                <div>
                  <p className="fs-1 fw-bold text-primary mb-7">
                    {editionData?.name || "...."}
                    {/* Request your invite */}
                  </p>

                  <div className="text-start fw-semi-bold text-primary mb-10">
                    You are requesting an invite to join our tribe of 5000+
                    creators, artists, founders, rebels, misfits, and doers on
                    adventures, brain jams & discoveries across the world.
                    Whether you’re joining for BHX (work-focused experiences) or
                    Bucketlist (travel-focused experiences), this form helps us
                    curate your invite.
                  </div>

                  <div className="bg-light p-3 rounded mb-7 w-75">
                    Already have an account?{" "}
                    <a
                      className="text-primary fw-bold"
                      href={getStoreLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Sign in
                    </a>{" "}
                    {/* <a
                      className="text-primary fw-bold"
                      href="https://play.google.com/store/apps/details?id=com.bhx.app"
                    >
                      Sign in
                    </a> */}
                  </div>
                </div>
                <div>
                  <p className="fw-bold fs-1 text-primary mb-2">About you</p>
                  <p className="text-primary mb-8">Tell us about you</p>
                  <InputField
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    required={true}
                  />
                  {/* <InputField
                    label="Middle Name"
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    placeholder="Middle name As on passport"
                  /> */}
                  <InputField
                    label="Last Name"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name "
                    required={true}
                  />
                  <CustomSelect
                    label="Gender (as on passport)"
                    options={genderOptions}
                    placeholder="Select Gender"
                    // options={[
                    //   { label: "Female", value: 1 },
                    //   { label: "Male", value: 2 },
                    //   { label: "Queer/Nonconforming", value: 3 },
                    //   { label: "Trans", value: 4 },
                    //   { label: "Prefer not to say", value: 5 },
                    // ]}
                    value={formData.gender}
                    onChange={(value) => handleSelectChange(value, "gender")}
                    required={true}
                  />

                  {/* <CustomSelect
                    label="City"
                    placeholder="Search City..."
                    value={formData.city}
                    onFocus={handleFocus}
                    onChange={(value) => handleSelectChange(value, "city")}
                    options={cities}
                    multiple={false}
                    onInputChange={(e) => {
                      setSearchTerm(e);
                    }}
                    loading={cityLoading}
                    onMenuScrollToBottom={(e) => {
                      if (!cityLoading) {
                        setActiveCityPage((prevState) => prevState + 1);
                      }
                    }}
                    // menuIsOpen={cities.length > 0}
                  />

                  <CustomSelect
                    ref={inputRef} // Reference to Select component
                    inputRef={(ref) => {
                      console.log(ref, "ref");
                      if (ref) {
                        inputRef.current = ref;
                      }
                    }}
                    label="City"
                    placeholder="Search City..."
                    value={formData.city}
                    onInputChange={(e) => {
                      console.log(e, "value");
                      handleInputChange(e);
                    }}
                    // onChange={(value) => {
                    //   handleCityChange(value);
                    // }}
                    onChange={(value) => {
                      console.log(value, "value");
                      handleCityChange(value);
                      // Auto-focus and move cursor to end after selection
                      setTimeout(() => {
                        if (inputRef.current) {
                          console.log(inputRef.current);
                          const input = inputRef.current.querySelector("input");
                          if (input) {
                            input.focus();
                            const len = value ? value.length : 0;
                            input.setSelectionRange(len, len);
                          }
                        }
                      }, 0);
                    }}
                    onFocus={(e) => {
                      console.log(e);

                      console.log(e.target);

                      console.log(e.target.tagName);

                      setTimeout(() => {
                        console.log(e.target.tagName);
                        if (e.target.tagName === "INPUT") {
                          const input = e.target;
                          // input.value = "StaticValue";
                          input.style.color = "red";

                          // console.log(input);
                          // console.log(input.value);

                          // input.setSelectionRange(10, 10); // Move cursor to position 10
                        }
                      }, 0);
                    }}
                    // onChange={(value) => handleSelectChange(value, "city")}
                    options={cities}
                    multiple={false}
                    // onInputChange={(e) => {
                    //   setSearchTerm(e);
                    // }}
                    loading={cityLoading}
                    onMenuScrollToBottom={(e) => {
                      if (!cityLoading) {
                        setActiveCityPage((prevState) => prevState + 1);
                      }
                    }}
                    // menuIsOpen={menuOpen && cities.length > 0}
                  /> */}

                  <CustomSelect
                    ref={inputRef} // Reference to Select component
                    inputRef={(ref) => {
                      if (ref) {
                        inputRef.current = ref;
                      }
                    }}
                    label="City"
                    placeholder="Search City..."
                    value={formData.city}
                    // inputValue={searchTerm}
                    // {...(!menuOpen && isFocused
                    //   ? { inputValue: searchTerm }
                    //   : {})}
                    onInputChange={(newValue, actionMeta) => {
                      setIsFocused(false);
                      // console.log(newValue, actionMeta);
                      if (actionMeta.action === "input-change") {
                        handleInputChange(newValue); // Keep track of input
                      }
                    }}
                    onChange={(value) => {
                      handleCityChange(value);

                      // Get cursor position before updating value
                      setTimeout(() => {
                        if (inputRef.current) {
                          const input = inputRef.current.querySelector("input");
                          if (input) {
                            input.focus();
                            const cursorPosition =
                              input.selectionStart + (value?.value.length || 0);
                            input.setSelectionRange(
                              cursorPosition,
                              cursorPosition
                            );
                          }
                        }
                      }, 0);
                    }}
                    onFocus={(e) => {
                      setIsFocused(true);
                      // setSearchTerm("Ahmedabad");
                      // setTimeout(() => {
                      //   if (e.target.tagName === "INPUT") {
                      //     const input = e.target;
                      //     input.style.color = "red";
                      //   }
                      // }, 0);
                    }}
                    options={cities}
                    multiple={false}
                    loading={cityLoading}
                    onMenuScrollToBottom={() => {
                      if (!cityLoading) {
                        setActiveCityPage((prevState) => prevState + 1);
                      }
                    }}
                    menuIsOpen={menuOpen && cities.length > 0}
                  />

                  {/* <CustomSelect
                    ref={inputRef} // Reference to Select component
                    inputRef={(ref) => {
                      if (ref) {
                        inputRef.current = ref;
                      }
                    }}
                    label="City"
                    placeholder="Search City..."
                    {...(!menuOpen ? { value: formData.city } : {})}
                    // value={formData.city}
                    inputValue={searchTerm}
                    // {...(isFocused ? { inputValue: searchTerm } : {})}
                    onInputChange={(newValue, actionMeta) => {
                      setIsFocused(false);
                      // console.log(newValue, actionMeta);
                      if (actionMeta.action === "input-change") {
                        handleInputChange(newValue); // Keep track of input
                      }
                    }}
                    onChange={(value) => {
                      handleCityChange(value);

                      // Get cursor position before updating value
                      setTimeout(() => {
                        if (inputRef.current) {
                          const input = inputRef.current.querySelector("input");
                          if (input) {
                            input.focus();
                            const cursorPosition =
                              input.selectionStart + (value?.value.length || 0);
                            input.setSelectionRange(
                              cursorPosition,
                              cursorPosition
                            );
                          }
                        }
                      }, 0);
                    }}
                    onFocus={(e) => {
                      setIsFocused(true);
                      // setSearchTerm("Ahmedabad");
                      // setTimeout(() => {
                      //   if (e.target.tagName === "INPUT") {
                      //     const input = e.target;
                      //     input.style.color = "red";
                      //   }
                      // }, 0);
                    }}
                    options={cities}
                    multiple={false}
                    loading={cityLoading}
                    onMenuScrollToBottom={() => {
                      if (!cityLoading) {
                        setActiveCityPage((prevState) => prevState + 1);
                      }
                    }}
                    menuIsOpen={menuOpen && cities.length > 0}
                  /> */}

                  {/* <DateInputField
                    label="Date of Birth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={(date) =>
                      setFormData({ ...formData, dateOfBirth: date })
                    }
                    placeholder="Enter Date of Birth"
                    required={true}
                  /> */}

                  <InputField
                    label="Date of Birth"
                    type="text"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters

                      if (value.length > 8) value = value.slice(0, 8); // Limit to 8 characters

                      let formattedValue = "";
                      if (value.length > 4) {
                        formattedValue = `${value.slice(0, 2)}/${value.slice(
                          2,
                          4
                        )}/${value.slice(4)}`;
                      } else if (value.length > 2) {
                        formattedValue = `${value.slice(0, 2)}/${value.slice(
                          2
                        )}`;
                      } else {
                        formattedValue = value;
                      }

                      setFormData({
                        ...formData,
                        [e.target.name]: formattedValue,
                      });
                    }}
                    placeholder="Date of Birth (DD/MM/YYYY)"
                  />
                  {/* <CustomSelect
                    label="Nationality"
                    options={[
                      { value: "in", label: "India" },
                      { value: "us", label: "United States" },
                      { value: "uk", label: "United Kingdom" },
                    ]}
                    placeholder="Select Nationality"
                    value={formData.nationality}
                    onChange={(value) =>
                      handleSelectChange(value, "nationality")
                    }
                  /> */}
                  <InputField
                    label="Designation"
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="Designation"
                    style={{ marginTop: "20px" }}
                    required={true}
                  />
                  <p
                    className="fw-bold fs-1 text-primary mt-8 mb-2"
                    style={{ marginTop: "20px" }}
                  >
                    What do you do?
                  </p>
                  <p className="text-primary mb-8 mb-5">
                    Tell us about yourself
                  </p>
                  {/* <InputField
                    label="Your Job Title"
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    placeholder="e.g., Software Engineer"
                  /> */}
                  <CustomSelect
                    label="Industry"
                    options={fieldOfWorkChoices}
                    placeholder="Pick your field of work"
                    value={formData.industry}
                    onChange={(value) => handleSelectChange(value, "industry")}
                    required={true}
                  />
                  <CustomSelect
                    label="Relevant Category"
                    options={categoryChoices}
                    placeholder="Pick a relevant category"
                    value={formData.relevant_category}
                    onChange={(value) =>
                      handleSelectChange(value, "relevant_category")
                    }
                    required={true}
                  />
                  <CustomSelect
                    label="Annual Revenue"
                    options={revenueChoices}
                    placeholder="Pick your annual revenue"
                    value={formData.annual_revenue}
                    onChange={(value) =>
                      handleSelectChange(value, "annual_revenue")
                    }
                    required={true}
                  />
                  <p className="fw-bold fs-1 text-primary mt-8 mb-2">
                    Your why
                  </p>
                  <p className="text-primary mb-8">
                    Your intention & story (160 characters or more)
                    <span className="required-astrix">*</span>
                  </p>
                  <InputField
                    label="Your Why"
                    type="url"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    // onChange={(e) => {
                    //   const value = e.target.value;
                    //   if (value.length <= 160) {
                    //     setFormData({ ...formData, bio: value });
                    //   }
                    // }}
                    placeholder="Tell us about yourself, what's your story and about the work you do"
                    rows={2}
                    multiline={true}
                  />
                  <p className="fw-bold fs-4 text-primary mt-5 mb-5 position-relative">
                    Top Reasons To Attend Experience (Pick any 2)
                    <span className="required-astrix">*</span>
                  </p>
                  {userChoices &&
                    Object.keys(userChoices).length > 0 &&
                    userChoices["top_priority_choices"].map((option, index) => (
                      <div key={index} className="form-check">
                        <input
                          type="checkbox"
                          id={`area-${index}`}
                          name="top_reasons"
                          value={option.value}
                          checked={formData.top_reasons.includes(option.value)}
                          onChange={handleChange}
                          className="form-check-input mt-2"
                        />
                        <label
                          htmlFor={`area-${index}`}
                          className="form-check-label text-primary mt-2"
                        >
                          {option.display_name}
                        </label>
                      </div>
                    ))}

                  <p className="fw-bold fs-4 text-primary mt-5 mb-5 position-relative">
                    Growth Areas (Pick any 2)
                    <span className="required-astrix">*</span>
                  </p>
                  {/* <p className="text-primary mb-4">Tell us about you</p> */}

                  {userChoices &&
                    Object.keys(userChoices).length > 0 &&
                    userChoices["growth_areas_choices"].map((option, index) => (
                      <div key={index} className="form-check">
                        <input
                          type="checkbox"
                          id={`reason-${index}`}
                          name="top_areas"
                          value={option.display_name}
                          checked={formData.top_areas.includes(
                            option.display_name
                          )}
                          onChange={handleChange}
                          className="form-check-input mt-2"
                        />
                        <label
                          htmlFor={`reason-${index}`}
                          className="form-check-label text-primary mt-2"
                        >
                          {option.display_name}
                        </label>
                      </div>
                    ))}

                  {/* <CustomSelect
                    label="Top2"
                    options={topPriorityChoices}
                    placeholder="Top 2 reasons to attend this experience"
                    value={formData.top_reasons}
                    onChange={(value) =>
                      handleSelectChange(value, "top_reasons")
                    }
                    multiple={true}
                  />

                  <CustomSelect
                    label="Top 2 Area to Grow"
                    options={growthAreasChoices}
                    placeholder="Top 2 areas you want to grow in"
                    value={formData.top_areas}
                    onChange={(value) => handleSelectChange(value, "top_areas")}
                    multiple={true}
                  /> */}
                  <p className="fw-bold fs-1 text-primary mt-8 my-3">Socials</p>
                  <p className="text-primary mb-8 ">
                    helps in curating your invite faster
                  </p>
                  <InputField
                    label="Instagram Profile"
                    type="url"
                    name="instagramProfile"
                    value={formData.instagramProfile}
                    onChange={(e) => {
                      const filteredValue = e.target.value.replace(/@/g, "");

                      setFormData((prevData) => ({
                        ...prevData,
                        instagramProfile: filteredValue,
                      }));
                    }}
                    placeholder="Enter your instagram handle"
                  />
                  <InputField
                    label="LinkedIn Profile"
                    type="url"
                    name="linkedinProfile"
                    value={formData.linkedinProfile}
                    onChange={handleChange}
                    placeholder="linkedin.com"
                  />
                  <InputField
                    label="Company/Portfolio"
                    type="url"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleChange}
                    placeholder="Enter your website URL"
                  />

                  {/* Phone Number Input */}
                  <div className="d-flex align-items-start border-bottom">
                    <div
                      style={{
                        width: "25%",
                        marginRight: "5px",
                        marginBottom: "6px",
                      }}
                    >
                      <CustomSelect
                        options={phoneCode.map((item) => ({
                          value: item.code,
                          label: item.country,
                          flag: item.flag,
                          code: item.code,
                        }))}
                        placeholder="+91"
                        formatOptionLabel={(
                          { label, flag, code },
                          { context, isSelected }
                        ) => (
                          <div
                            style={{
                              textAlign: "start",
                              display: "flex",
                              justifyContent: "start",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            {context === "menu" ? (
                              <>
                                <span style={{ fontSize: "14px" }}>{flag}</span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  {code}
                                </span>
                              </>
                            ) : (
                              <span
                                style={{
                                  color: "#666",
                                  fontSize: "14px",
                                }}
                              >
                                {code}
                              </span>
                            )}
                          </div>
                        )}
                        value={formData.countryCode}
                        onChange={(value) =>
                          handleSelectChange(value, "countryCode")
                        }
                      />

                      {/* <InputField
                        label={""} // Remove label if not needed visually
                        type="text"
                        id="countryCode"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            ""
                          ); // Remove non-numeric characters
                          handleChange({
                            target: {
                              name: "countryCode",
                              value: numericValue,
                            },
                          });
                        }}
                        // onChange={handleChange}
                        className="border-0 bg-transparent text-muted text-end"
                        placeholder="+91"
                      /> */}
                    </div>
                    <div style={{ width: "75%" }}>
                      <InputField
                        type="text"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onBlur={() => validateFields()}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            ""
                          ); // Remove non-numeric characters
                          handleChange({
                            target: {
                              name: "phoneNumber",
                              value: numericValue,
                            },
                          });

                          // setTimeout(() => {
                          //   validateFields();
                          // }, 1000);
                        }}
                        // onChange={handleChange}
                        className="border-0 bg-transparent flex-grow-1"
                        placeholder="Phone number"
                        style={{ width: "85%", marginRight: "5px" }}
                        required={true}
                        // rows={2}
                      />
                    </div>
                  </div>
                  {errors.phoneNumber && (
                    <p
                      className="mt-n6"
                      style={{
                        color: "red",
                        fontSize: "10px",
                        textAlign: "end",
                      }}
                    >
                      {errors.phoneNumber}
                    </p>
                  )}
                  {/* Email ID Input */}
                  <div className="mb-3">
                    <InputField
                      label={"Email"}
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      // onChange={(e) => {
                      //   handleChange(e);
                      //   // setTimeout(() => {
                      //   //   validateFields();
                      //   // }, 1000);
                      // }}
                      onBlur={() => validateFields()}
                      className="form-control"
                      placeholder="Email ID"
                      required={true}
                    />
                    {errors.email && (
                      <p
                        className="mt-n5"
                        style={{
                          color: "red",
                          fontSize: "10px",
                          textAlign: "end",
                        }}
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>
                  {/* Referral Checkbox and Code Input */}
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      id="hasReferral"
                      name="hasReferral"
                      checked={formData.hasReferral}
                      onChange={handleChange}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="hasReferral"
                      className="form-check-label"
                      style={{ marginBottom: "15px" }}
                    >
                      I have a referral from a tribe member
                    </label>
                  </div>
                  {formData.hasReferral && (
                    <div className="my-">
                      <label htmlFor="referralCode" className="form-label">
                        Enter the referral code here
                      </label>
                      <InputField
                        type="text"
                        id="referralCode"
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleChange}
                        className="form-control"
                        placeholder={"Referral Code"}
                      />
                    </div>
                  )}
                  {/* Contact Preferences */}
                  <div className="mb-3">
                    <label className="form-label">
                      How do you prefer to receive updates?
                    </label>
                    <div style={{ marginBottom: "15px" }}>
                      <div className="form-check form-check-inline">
                        <input
                          type="checkbox"
                          id="phone"
                          name="contactMethod"
                          value="phone"
                          checked={formData.contactMethod.includes("phone")}
                          onChange={handleChange}
                          className="form-check-input"
                        />
                        <label className="form-check-label" htmlFor="phone">
                          Phone
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          type="checkbox"
                          id="text"
                          name="contactMethod"
                          value="text"
                          checked={formData.contactMethod.includes("text")}
                          onChange={handleChange}
                          className="form-check-input"
                        />
                        <label className="form-check-label" htmlFor="text">
                          Text
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          type="checkbox"
                          id="email"
                          name="contactMethod"
                          value="email"
                          checked={formData.contactMethod.includes("email")}
                          onChange={handleChange}
                          className="form-check-input"
                        />
                        <label className="form-check-label" htmlFor="email">
                          Email
                        </label>
                      </div>
                    </div>
                  </div>
                  {/* How Did You Hear About Us Dropdown */}
                  <div className="mb-3">
                    <label htmlFor="howHeard" className="form-label">
                      How did you hear about us?
                    </label>
                    <CustomSelect
                      label="How Heard"
                      options={[
                        { value: "Instagram", label: "Instagram" },
                        { value: "Email", label: "Email" },
                        { value: "Tribe", label: "Tribe" },
                        {
                          value: "My friend told me about it",
                          label: "My friend told me about it",
                        },
                        // { value: "Friends", label: "Friends" },
                        // { value: "Social Media", label: "Social Media" },
                        // { value: "Advertisement", label: "Advertisement" },
                      ]}
                      placeholder="Pick an option"
                      value={formData.howHeard}
                      onChange={(value) =>
                        handleSelectChange(value, "howHeard")
                      }
                      required={true}
                    />
                  </div>

                  <ButtonWrapper
                    className="btn w-100 mt-4 py-2"
                    type="submit"
                    onClick={handleSubmit}
                    // onClick={() => {
                    //   const userData = {
                    //     email: "john.doe@example.com",
                    //     phone: "+1234567890",
                    //     dob: "1990-06-15",
                    //     first_name: "John",
                    //     city: "New York",
                    //     country: "IN", // Country = India
                    //     gender: "Male",
                    //     user_id: "user_12345",
                    //     client_ip: "192.168.1.1",
                    //     user_agent:
                    //       "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
                    //     access_token: "your-access-token-here",
                    //   };

                    //   const hashedFields = {
                    //     email: hashData(userData.email),
                    //     phone: hashData(userData.phone),
                    //     dob: hashData(userData.dob),
                    //     first_name: hashData(userData.first_name),
                    //     city: hashData(userData.city),
                    //     country: hashData(userData.country),
                    //     gender: hashData(userData.gender),
                    //     user_id: hashData(userData.user_id),
                    //     client_ip: hashData(userData.client_ip),
                    //     user_agent: hashData(userData.user_agent),
                    //     access_token: hashData(userData.access_token), // Hash access token
                    //   };

                    //   // if (window.fbq) {
                    //   //   window.fbq("track", "Submit Application", hashedFields);
                    //   // }

                    //   ReactPixel.track("Submit Application", hashedFields);
                    // }}
                    disabled={bookingError || sending}
                  >
                    Submit {sending && <Spinner />}
                  </ButtonWrapper>

                  {/* <button
                    type="submit"
                    onClick={handleSubmit}
                    style={{ backgroundColor: "#FE664E", color: "white" }}
                    className="btn w-100 mt-4 py-2"
                  >
                    Submit
                  </button> */}
                </div>
              </div>

              <div className="col-md-4  pe-lg-5"></div>

              <div className="d-none d-lg-block col-md-4 pe-lg-5 mt-5 mt-lg-0">
                <div
                  className="card shadow-sm p-8 bg-light"
                  style={{ maxWidth: "400px" }}
                >
                  <h5 className="fw-bolder text-dark fs-2 mb-7">
                    Your experience
                  </h5>
                  <h6 className="text-primary fw-bolder mb-4">
                    {/* {editionData?.project_name} */}
                    About your edition
                  </h6>
                  <p className="text-secondary fw-semibold mb-4">
                    {editionData?.description || "......"}
                  </p>
                  <div className="d-flex justify-content-start align-items-center mt-4">
                    <div className="text-center">
                      <div className="badge bg-white p-4">
                        {editionData?.start_date
                          ? formatDate(editionData?.start_date)
                          : "-"}
                      </div>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      className="mx-3"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.2634 14.0004L9.88213 20.3817L11.1196 21.6191L18.7383 14.0004L11.1196 6.3817L9.88213 7.61914L16.2634 14.0004Z"
                        fill="#856471"
                      />
                    </svg>
                    <div className="text-center">
                      <div className="badge bg-white p-4">
                        {editionData?.end_date
                          ? formatDate(editionData?.end_date)
                          : "-"}
                      </div>
                    </div>
                  </div>
                  <div className="text-primary fw-bold fs-8 mb-5 mt-2">
                    <div className="text-primary fw-bold fs-8 mb-5 mt-2">
                      {/* Calculate the duration */}
                      {(() => {
                        const startDate = new Date(editionData?.start_date);
                        const endDate = new Date(editionData?.end_date);

                        // Calculate the difference in milliseconds
                        const durationInMilliseconds = endDate - startDate;

                        // Convert milliseconds to days
                        const days =
                          Math.ceil(
                            durationInMilliseconds / (1000 * 60 * 60 * 24)
                          ) + 1; // Inclusive of start and end dates

                        // Calculate nights
                        const nights = days - 1;

                        return `${days || 0} days, ${nights || 0} nights`;
                      })()}
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-bold">Inclusions</h6>
                    <ul className="list-unstyled">
                      <li className="d-flex align-items-center mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="me-3"
                        >
                          <path
                            d="M9.72333 15.8962C9.48516 15.9199 9.24359 15.932 8.99919 15.932C4.99989 15.932 1.75781 12.6899 1.75781 8.6906C1.75781 4.69129 4.99989 1.44922 8.99919 1.44922C12.9985 1.44922 16.2406 4.69129 16.2406 8.6906C16.2406 8.93499 16.2285 9.17657 16.2048 9.41474"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                          />
                          <path
                            d="M5.73828 12.3097C6.75386 11.2461 8.28812 10.792 9.72104 11.002M10.8037 6.8787C10.8037 7.87852 9.99201 8.68905 8.99075 8.68905C7.98955 8.68905 7.17785 7.87852 7.17785 6.8787C7.17785 5.87888 7.98955 5.06836 8.99075 5.06836C9.99201 5.06836 10.8037 5.87888 10.8037 6.8787Z"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                          />
                          <path
                            d="M13.7064 15.9303C15.1061 15.9303 16.2408 14.7956 16.2408 13.3958C16.2408 11.9961 15.1061 10.8613 13.7064 10.8613C12.3066 10.8613 11.1719 11.9961 11.1719 13.3958C11.1719 14.7956 12.3066 15.9303 13.7064 15.9303Z"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                          />
                        </svg>
                        <span className="text-primary fw-semibold">
                          Once-in-a-lifetime experience
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="19"
                          viewBox="0 0 18 19"
                          fill="none"
                          className="me-3"
                        >
                          <path
                            d="M16.2406 6.66602C16.2406 6.66602 13.344 11.0108 8.99919 11.0108C4.65436 11.0108 1.75781 6.66602 1.75781 6.66602"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                          />
                          <path
                            d="M11.1719 10.6484L12.2581 12.4588"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M14.793 8.83789L16.2412 10.2862"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M1.75781 10.2862L3.20609 8.83789"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M6.82449 10.6484L5.73828 12.4588"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        <span className="text-primary fw-semibold">
                          Facilitated by founding team
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="13"
                          viewBox="0 0 15 13"
                          fill="none"
                          className="me-3"
                        >
                          <path
                            d="M0.0673574 11.4479V5.93043C0.0673574 5.74096 0.108559 5.55704 0.190963 5.37868C0.274 5.20091 0.384611 5.04828 0.522796 4.9208L3.74034 1.95243C3.89374 1.81091 4.06489 1.7077 4.25378 1.64278C4.44331 1.57729 4.63252 1.54454 4.82141 1.54454C5.01031 1.54454 5.1992 1.57729 5.3881 1.64278C5.57699 1.70828 5.74814 1.8115 5.90154 1.95243L9.12003 4.92167C9.25758 5.04857 9.36788 5.2012 9.45091 5.37956C9.53395 5.55792 9.57515 5.74154 9.57452 5.93043V11.4479C9.57452 11.8408 9.42524 12.1753 9.12669 12.4514C8.82687 12.7274 8.46429 12.8654 8.03896 12.8654H1.60292C1.17759 12.8654 0.815329 12.7274 0.51614 12.4514C0.216318 12.1753 0.0664062 11.8408 0.0664062 11.4479M1.60292 11.9873H4.08929V10.6382C4.08929 10.457 4.15902 10.2991 4.29847 10.1646C4.43792 10.0301 4.61224 9.96281 4.82141 9.96281C5.01792 9.96281 5.18874 10.0301 5.3339 10.1646C5.47906 10.2991 5.55195 10.457 5.55259 10.6382V11.9873H8.03896C8.21011 11.9873 8.35051 11.9368 8.46017 11.8356C8.56983 11.7344 8.62466 11.6052 8.62466 11.4479V5.91288C8.62466 5.84563 8.6123 5.78101 8.58758 5.71903C8.56349 5.65704 8.52102 5.59798 8.46017 5.54184L5.24167 2.57259C5.13201 2.47143 4.99193 2.42084 4.82141 2.42084C4.65027 2.42084 4.50987 2.47143 4.4002 2.57259L1.18266 5.54184C1.12181 5.59798 1.07902 5.65704 1.0543 5.71903C1.02958 5.78101 1.01722 5.84563 1.01722 5.91288V11.4479C1.01722 11.6052 1.07236 11.7344 1.18266 11.8356C1.29169 11.9368 1.43177 11.9873 1.60292 11.9873ZM11.111 12.4435V4.97167C11.111 4.90442 11.0987 4.8398 11.0739 4.77782C11.0492 4.71583 11.0068 4.65677 10.9465 4.60063L7.80696 1.70419C7.6523 1.5615 7.61426 1.40098 7.69286 1.22262C7.77273 1.04426 7.92296 0.955078 8.14355 0.955078C8.20947 0.955078 8.27222 0.966774 8.33181 0.990165C8.39139 1.01356 8.44274 1.04455 8.48584 1.08315L11.6064 3.96292C11.7446 4.08982 11.8549 4.24245 11.9373 4.4208C12.0197 4.59916 12.0612 4.78279 12.0618 4.97167V12.4426C12.0618 12.5677 12.0165 12.6721 11.9259 12.7557C11.8352 12.8394 11.7221 12.8812 11.5864 12.8812C11.4508 12.8812 11.3376 12.8394 11.247 12.7557C11.1564 12.6721 11.111 12.5677 11.111 12.4426M13.5974 12.4426V4.01379C13.5974 3.94654 13.5854 3.88222 13.5613 3.82082C13.5372 3.75941 13.4944 3.70006 13.4329 3.64275L11.3506 1.72173C11.196 1.57904 11.1579 1.4156 11.2365 1.23139C11.3151 1.04718 11.4654 0.95537 11.6872 0.955955C11.7531 0.955955 11.8159 0.967651 11.8755 0.991043C11.9351 1.01385 11.9864 1.04484 12.0295 1.08402L14.1118 3.00504C14.2493 3.13252 14.3565 3.28545 14.4332 3.4638C14.5099 3.64216 14.5485 3.82549 14.5492 4.01379V12.4435C14.5492 12.5686 14.5035 12.673 14.4122 12.7566C14.3216 12.8402 14.2088 12.8821 14.0738 12.8821C13.9381 12.8821 13.825 12.8402 13.7343 12.7566C13.6437 12.673 13.598 12.5686 13.5974 12.4435M1.60292 11.9873H8.62466H1.01817H1.60292ZM4.82141 7.3997C4.62491 7.3997 4.45377 7.33245 4.30798 7.19795C4.16218 7.06345 4.08929 6.90556 4.08929 6.72427C4.08929 6.54299 4.16218 6.3851 4.30798 6.2506C4.45377 6.1161 4.62491 6.04885 4.82141 6.04885C5.01792 6.04885 5.18874 6.1161 5.3339 6.2506C5.47906 6.3851 5.55195 6.54299 5.55259 6.72427C5.55322 6.90556 5.48033 7.06345 5.3339 7.19795C5.18748 7.33245 5.01633 7.3997 4.82046 7.3997"
                            fill="#5F2C41"
                          />
                        </svg>
                        <span className="text-primary fw-semibold">
                          Gorgeous airbnbs & luxury hotels
                        </span>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="me-3"
                        >
                          <path
                            d="M11.1705 2.07031L11.5606 3.80214C11.8916 5.27111 13.0387 6.41826 14.5076 6.74916L16.2395 7.13928L14.5076 7.52939C13.0387 7.86029 11.8916 9.00741 11.5606 10.4764L11.1705 12.2082L10.7804 10.4764C10.4495 9.00741 9.3024 7.86029 7.83341 7.52939L6.10156 7.13928L7.83341 6.74916C9.30232 6.41826 10.4495 5.27111 10.7804 3.80215L11.1705 2.07031Z"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M5.3785 9.31055L5.65716 10.5476C5.89352 11.5968 6.71291 12.4162 7.76215 12.6526L8.99919 12.9312L7.76215 13.2099C6.71291 13.4462 5.89352 14.2656 5.65716 15.3149L5.3785 16.5519L5.09985 15.3149C4.86349 14.2656 4.0441 13.4462 2.99484 13.2099L1.75781 12.9312L2.99484 12.6526C4.0441 12.4162 4.86349 11.5969 5.09985 10.5476L5.3785 9.31055Z"
                            stroke="#5F2C41"
                            stroke-width="1.08621"
                            stroke-linejoin="round"
                          />
                        </svg>
                        <span className="text-primary fw-semibold">
                          {`${editionData?.invite_count || 0} Curated invitees`}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestInvite;
